import React, { useMemo } from "react";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
  GetProcessesResponseBodyType,
  GraphConfig,
  OrganizationDto,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { DashboardPageName } from "@src/api/types/dashboards";
import { useCanViewTestAnalytics } from "@src/components/LimitAccess/check-analytics-access";
import { Processes } from "@src/components/MainApp/Alerts/Processes";
import { AnalyticRecommendation } from "@src/components/recommendation/AnalyticsRecommendation";
import { AnalyticsSummary } from "@src/features/ai/components/usages/AnalyticsSummary";
import { AnalyticsAccessDenied } from "@src/features/analytics/AnalyticsAccessDenied";
import { AnalyticsIntegrationsAlert } from "@src/features/analytics/baseDashboard/AnalyticsIntegrationsAlert";
import { ExperienceFloatingDetailsBar } from "@src/features/analytics/baseDashboard/ExperienceFloatingDetailsBar";
import { CheckoutUpsellAnalyticsPage } from "@src/features/analytics/pages/CheckoutUpsellAnalyticsPage";
import { ConversionAnalyticsPage } from "@src/features/analytics/pages/ConversionAnalyticsPage";
import { CustomMetricsAnalyticsPage } from "@src/features/analytics/pages/CustomMetricsAnalyticsPage";
import { DiscountAnalyticsPage } from "@src/features/analytics/pages/DiscountAnalyticsPage";
import { KeyMetricsAnalyticsPage } from "@src/features/analytics/pages/KeyMetricsAnalyticsPage";
import { OnsiteUpsellAnalyticsPage } from "@src/features/analytics/pages/OnsiteUpsellAnalyticsPage";
import { OrderAnalyticsPage } from "@src/features/analytics/pages/OrderAnalyticsPage";
import { OrdersReconciliationAnalyticsPage } from "@src/features/analytics/pages/OrdersReconciliationAnalyticsPage";
import { PostTestMetricsAnalyticsPage } from "@src/features/analytics/pages/PostTestMetricsAnalyticsPage";
import { ProductGroupAnalyticsPage } from "@src/features/analytics/pages/ProductGroupAnalyticsPage";
import { ProfitAnalyticsPage } from "@src/features/analytics/pages/ProfitAnalyticsPage";
import { RevenueAnalyticsPage } from "@src/features/analytics/pages/RevenueAnalyticsPage";
import { ShippingAnalyticsPage } from "@src/features/analytics/pages/ShippingAnalyticsPage";
import { SubscriptionAnalyticsPage } from "@src/features/analytics/pages/SubscriptionAnalyticsPage";
import { UseQueryResult } from "@tanstack/react-query";

interface AnalyticsDashboardViewProps {
  // Required - core analytics data
  pageName: DashboardPageName;
  analyticsData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  experienceTotals: {
    visitors: number;
    orders: number;
    revenue: number | null;
  };
  testDuration: number;
  compareVariationId: string;
  isLoading: boolean;
  shouldNotRenderGraphs: boolean;

  // Optional - for floating details bar
  latestRunTs?: number;
  experience?: ExperienceFe;
  timezone?: string;

  // Desktop-specific attributes (preserved for Chameleon tours, analytics)
  dataAnalyticsHasData?: string;
  containerId?: string;

  // Optional features (all default to false for share view)
  showFloatingBar?: boolean;
  showAiSummary?: boolean;
  showIntegrationsAlert?: boolean;
  showProcesses?: boolean;

  // Data for optional features (only needed if enabled)
  organization?: OrganizationDto;
  processes?: UseQueryResult<GetProcessesResponseBodyType>;
  startedOverFourHoursAgo?: boolean;
  overviewSummaryData?: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];

  // State for integrations alert
  integrationsAlertOpen?: boolean;
  setIntegrationsAlertOpen?: (open: boolean) => void;
}

export const AnalyticsDashboardView = React.memo(
  ({
    pageName,
    analyticsData,
    graphConfigs,
    experienceTotals,
    testDuration,
    compareVariationId,
    isLoading,
    shouldNotRenderGraphs,
    latestRunTs,
    experience,
    timezone,
    dataAnalyticsHasData,
    containerId,
    showFloatingBar = false,
    showAiSummary = false,
    showIntegrationsAlert = false,
    showProcesses = false,
    organization,
    processes,
    startedOverFourHoursAgo,
    overviewSummaryData,
    integrationsAlertOpen,
    setIntegrationsAlertOpen,
  }: AnalyticsDashboardViewProps) => {
    // Page selection logic
    const AnalyticsPage = useMemo(() => {
      switch (pageName) {
        case "overview":
          return KeyMetricsAnalyticsPage;
        case "conversion":
          return ConversionAnalyticsPage;
        case "shipping":
          return ShippingAnalyticsPage;
        case "profit":
          return ProfitAnalyticsPage;
        case "revenue":
          return RevenueAnalyticsPage;
        case "subscriptions":
          return SubscriptionAnalyticsPage;
        case "discounts":
          return DiscountAnalyticsPage;
        case "orders":
          return OrderAnalyticsPage;
        case "productGroups":
          return ProductGroupAnalyticsPage;
        case "customMetrics":
          return CustomMetricsAnalyticsPage;
        case "dataReconciliation":
          return OrdersReconciliationAnalyticsPage;
        case "postTestMetrics":
          return PostTestMetricsAnalyticsPage;
        case "checkoutUpsell":
          return CheckoutUpsellAnalyticsPage;
        case "onsiteUpsell":
          return OnsiteUpsellAnalyticsPage;
        default:
          return KeyMetricsAnalyticsPage;
      }
    }, [pageName]);
    const canViewTestAnalytics = useCanViewTestAnalytics();
    if (!canViewTestAnalytics) {
      return <AnalyticsAccessDenied />;
    }

    // Early return for desktop when no data to show
    if (shouldNotRenderGraphs && !isLoading) {
      return <Grid mt={8}></Grid>;
    }

    return (
      <Stack
        data-analytics-has-data={dataAnalyticsHasData}
        id={containerId}
        direction="column"
        spacing={3}
        sx={{ width: "100%" }}
      >
        {/* Processes component - desktop only */}
        {showProcesses &&
        processes?.isSuccess &&
        processes.data?.processes.filter(
          (process) => process.type === "downloadExperience"
        ).length > 0 ? (
          <Grid
            size={12}
            container
            mt={4}
            sx={{ width: "100%", marginBottom: "-16px" }}
          >
            <Processes
              deleteOnOpen={true}
              filterToSpecificKey={"downloadExperience"}
            />
          </Grid>
        ) : null}

        <AnalyticRecommendation pageName={pageName} />

        {/* Floating details bar - shown on overview page */}
        {showFloatingBar && pageName === "overview" && (
          <ExperienceFloatingDetailsBar
            latestRunTs={latestRunTs}
            experienceTotals={experienceTotals}
            shouldRenderGraphs={!shouldNotRenderGraphs || isLoading}
            hidePaperBorder={true}
            isDataLoading={isLoading}
            experience={experience}
            timezone={timezone}
          />
        )}

        {/* AI summary - desktop only, not on data reconciliation page */}
        {showAiSummary &&
          pageName !== "dataReconciliation" &&
          startedOverFourHoursAgo &&
          experience && (
            <AnalyticsSummary
              experience={experience}
              dashboardData={overviewSummaryData ?? analyticsData}
            />
          )}

        {/* Analytics page content */}
        <Stack direction="column" sx={{ width: "100%" }}>
          <AnalyticsPage
            data={analyticsData}
            testDuration={testDuration}
            totalExperienceVisitors={experienceTotals.visitors || 0}
            graphConfigs={graphConfigs}
            compareVariationId={compareVariationId}
          />

          {/* Integrations alert - desktop only */}
          {showIntegrationsAlert &&
            integrationsAlertOpen &&
            organization &&
            !organization.integrations.length &&
            setIntegrationsAlertOpen && (
              <AnalyticsIntegrationsAlert setOpen={setIntegrationsAlertOpen} />
            )}
        </Stack>
      </Stack>
    );
  }
);

AnalyticsDashboardView.displayName = "AnalyticsDashboardView";
